<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { API_BASE_URL } from '../config'

// 文件夹接口
interface Folder {
  id: number
  name: string
  parent_id: number
  sort_order: number
  file_count?: number
}

// 文件接口
interface FileItem {
  id: number
  folder_id: number
  name: string
  original_name: string
  type: 'folder' | 'doc' | 'pdf' | 'image' | 'video' | 'audio' | 'other'
  mime_type: string
  size: number
  size_format: string
  path: string
  url: string
  ext: string
  created_at: string
  updated_at: string
}

const folders = ref<Folder[]>([])
const files = ref<FileItem[]>([])
const activeFolder = ref(0)
const loading = ref(false)
const foldersLoading = ref(false)

// 获取文件夹列表
const fetchFolders = async () => {
  foldersLoading.value = true
  try {
    const response = await fetch(`${API_BASE_URL}/api/files/folders?parent_id=0`)
    const result = await response.json()
    if (result.code === 1 && result.data) {
      folders.value = [{ id: 0, name: '全部文件', parent_id: 0, sort_order: 0 }, ...result.data]
    }
  } catch (error) {
    console.error('获取文件夹列表失败:', error)
  } finally {
    foldersLoading.value = false
  }
}

// 获取文件列表
const fetchFiles = async (folderId: number = 0) => {
  loading.value = true
  try {
    const response = await fetch(`${API_BASE_URL}/api/files/list?folder_id=${folderId}`)
    const result = await response.json()
    if (result.code === 1 && result.data) {
      files.value = result.data
    } else {
      files.value = []
    }
  } catch (error) {
    console.error('获取文件列表失败:', error)
    files.value = []
  } finally {
    loading.value = false
  }
}

// 切换文件夹
const switchFolder = (folderId: number) => {
  activeFolder.value = folderId
  fetchFiles(folderId)
}

// 过滤文件列表
const filteredFiles = computed(() => {
  if (activeFolder.value === 0) {
    return files.value
  }
  return files.value.filter(file => file.folder_id === activeFolder.value)
})

// 获取文件夹文件数量
const getFolderCount = (folder: Folder) => {
  if (folder.id === 0) {
    // "全部文件"显示所有文件夹的文件数量之和
    return folders.value
      .filter(f => f.id !== 0)
      .reduce((sum, f) => sum + (f.file_count || 0), 0)
  }
  return folder.file_count || 0
}

// 获取文件图标
const getFileIcon = (type: string) => {
  const icons: Record<string, string> = {
    folder: '📁',
    doc: '📄',
    pdf: '📕',
    image: '🖼️',
    video: '🎬',
    audio: '🎵',
    other: '📎',
  }
  return icons[type] || '📄'
}

// 格式化日期
const formatDate = (dateStr: string) => {
  if (!dateStr) return ''
  return dateStr.slice(5, 16)
}

// 预览相关
const showPreview = ref(false)
const previewFile = ref<FileItem | null>(null)
const previewContent = ref('')
const previewLoading = ref(false)

// 打开预览
const openPreview = async (file: FileItem) => {
  previewFile.value = file
  showPreview.value = true
  previewLoading.value = true
  previewContent.value = ''
  
  // 文本文件加载内容
  if (file.type === 'doc' && file.ext === 'txt') {
    try {
      const response = await fetch(`${API_BASE_URL}${file.url}`)
      const text = await response.text()
      previewContent.value = text
    } catch (error) {
      previewContent.value = '文件加载失败'
    }
  }
  
  previewLoading.value = false
}

// 关闭预览
const closePreview = () => {
  showPreview.value = false
  previewFile.value = null
  previewContent.value = ''
}

// 判断是否可预览
const isPreviewable = (file: FileItem) => {
  return ['image', 'video', 'audio', 'pdf'].includes(file.type) || 
         (file.type === 'doc' && file.ext === 'txt')
}

// 获取完整文件URL
const getFullFileUrl = (url: string) => {
  if (url.startsWith('http')) return url
  return `${API_BASE_URL}${url}`
}

// 上传文件相关
const fileInputRef = ref<HTMLInputElement | null>(null)
const uploadLoading = ref(false)
const uploadProgress = ref(0)

// 触发文件选择
const triggerUpload = () => {
  fileInputRef.value?.click()
}

// 处理文件上传
const handleFileUpload = async (event: Event) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  if (!file) return
  
  uploadLoading.value = true
  uploadProgress.value = 0
  
  const formData = new FormData()
  formData.append('file', file)
  formData.append('folder_id', String(activeFolder.value))
  
  // 使用 XMLHttpRequest 以支持进度监控
  const xhr = new XMLHttpRequest()
  
  xhr.upload.addEventListener('progress', (e) => {
    if (e.lengthComputable) {
      uploadProgress.value = Math.round((e.loaded / e.total) * 100)
    }
  })
  
  xhr.addEventListener('load', () => {
    if (xhr.status === 200) {
      try {
        const result = JSON.parse(xhr.responseText)
        if (result.code === 1) {
          // 刷新文件列表
          fetchFiles(activeFolder.value)
          // 刷新文件夹列表（更新计数）
          fetchFolders()
        } else {
          alert(result.msg || '上传失败')
        }
      } catch (error) {
        console.error('解析响应失败:', error)
        alert('上传失败，请稍后重试')
      }
    } else {
      alert('上传失败，请稍后重试')
    }
    
    setTimeout(() => {
      uploadLoading.value = false
      uploadProgress.value = 0
      target.value = ''
    }, 500)
  })
  
  xhr.addEventListener('error', () => {
    alert('上传失败，请稍后重试')
    uploadLoading.value = false
    uploadProgress.value = 0
    target.value = ''
  })
  
  xhr.open('POST', `${API_BASE_URL}/api/files/upload`)
  xhr.send(formData)
}

// 新建文件夹
const showNewFolderDialog = ref(false)
const newFolderName = ref('')
const newFolderNameEn = ref('')
const creatingFolder = ref(false)

const createFolder = async () => {
  if (!newFolderName.value.trim()) return
  
  creatingFolder.value = true
  try {
    const response = await fetch(`${API_BASE_URL}/api/files/create-folder`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: newFolderName.value,
        folder: newFolderNameEn.value || newFolderName.value,
        parent_id: 0,
      }),
    })
    
    const result = await response.json()
    if (result.code === 1) {
      newFolderName.value = ''
      newFolderNameEn.value = ''
      showNewFolderDialog.value = false
      fetchFolders()
    } else {
      alert(result.msg || '创建失败')
    }
  } catch (error) {
    console.error('创建文件夹失败:', error)
    alert('创建失败，请稍后重试')
  } finally {
    creatingFolder.value = false
  }
}

// 删除文件/文件夹
const deleteItem = async (type: 'folder' | 'file', id: number) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/files/delete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type, id }),
    })
    
    const result = await response.json()
    if (result.code === 1) {
      if (type === 'folder') {
        fetchFolders()
        if (activeFolder.value === id) {
          activeFolder.value = 0
          fetchFiles(0)
        }
      } else {
        fetchFiles(activeFolder.value)
        fetchFolders()
      }
    } else {
      alert(result.msg || '删除失败')
    }
  } catch (error) {
    console.error('删除失败:', error)
    alert('删除失败，请稍后重试')
  }
}

// 右键菜单相关
const contextMenuVisible = ref(false)
const contextMenuX = ref(0)
const contextMenuY = ref(0)
const contextMenuType = ref<'folder' | 'file' | null>(null)
const contextMenuTarget = ref<Folder | FileItem | null>(null)
const renamingItem = ref<{ type: 'folder' | 'file'; id: number; name: string; folder?: string } | null>(null)
const renameValue = ref('')
const renameFolderValue = ref('')
const renameLoading = ref(false)

// 删除确认弹窗
const showDeleteConfirm = ref(false)
const deleteConfirmType = ref<'folder' | 'file' | null>(null)
const deleteConfirmTarget = ref<Folder | FileItem | null>(null)
const deleteLoading = ref(false)

// 显示文件夹右键菜单
const showFolderContextMenu = (event: MouseEvent, folder: Folder) => {
  // 不显示"全部文件"的右键菜单
  if (folder.id === 0) return
  
  contextMenuVisible.value = true
  contextMenuX.value = event.clientX
  contextMenuY.value = event.clientY
  contextMenuType.value = 'folder'
  contextMenuTarget.value = folder
}

// 显示文件右键菜单
const showFileContextMenu = (event: MouseEvent, file: FileItem) => {
  contextMenuVisible.value = true
  contextMenuX.value = event.clientX
  contextMenuY.value = event.clientY
  contextMenuType.value = 'file'
  contextMenuTarget.value = file
}

// 隐藏右键菜单
const hideContextMenu = () => {
  contextMenuVisible.value = false
  contextMenuType.value = null
  contextMenuTarget.value = null
}

// 下载文件
const downloadFile = () => {
  const file = contextMenuTarget.value as FileItem | null
  if (!file || contextMenuType.value !== 'file') return
  
  const link = document.createElement('a')
  link.href = getFullFileUrl(file.url)
  link.download = file.original_name
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  
  hideContextMenu()
}

// 开始重命名
const startRename = () => {
  if (!contextMenuTarget.value) return
  
  const target = contextMenuTarget.value
  const isFolder = contextMenuType.value === 'folder'
  renamingItem.value = {
    type: contextMenuType.value!,
    id: target.id,
    name: isFolder 
      ? (target as Folder).name 
      : (target as FileItem).original_name,
    folder: isFolder ? (target as Folder).name : undefined
  }
  renameValue.value = renamingItem.value.name
  renameFolderValue.value = renamingItem.value.folder || ''
  hideContextMenu()
}

// 执行重命名
const doRename = async () => {
  if (!renamingItem.value || !renameValue.value.trim()) return
  if (renameValue.value === renamingItem.value.name && renameFolderValue.value === (renamingItem.value.folder || '')) {
    renamingItem.value = null
    return
  }
  
  renameLoading.value = true
  try {
    const body: any = {
      type: renamingItem.value.type,
      id: renamingItem.value.id,
      name: renameValue.value.trim()
    }
    // 文件夹重命名时添加 folder 字段
    if (renamingItem.value.type === 'folder') {
      body.folder = renameFolderValue.value.trim() || renameValue.value.trim()
    }
    
    const response = await fetch(`${API_BASE_URL}/api/files/rename`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    
    const result = await response.json()
    if (result.code === 1) {
      // 刷新列表
      if (renamingItem.value.type === 'folder') {
        fetchFolders()
      } else {
        fetchFiles(activeFolder.value)
      }
      renamingItem.value = null
      renameFolderValue.value = ''
    } else {
      alert(result.msg || '重命名失败')
    }
  } catch (error) {
    console.error('重命名失败:', error)
    alert('重命名失败，请稍后重试')
  } finally {
    renameLoading.value = false
  }
}

// 取消重命名
const cancelRename = () => {
  renamingItem.value = null
  renameValue.value = ''
  renameFolderValue.value = ''
}

// 执行删除
const doDelete = () => {
  if (!contextMenuTarget.value || !contextMenuType.value) return
  
  // 文件夹和文件都使用弹窗确认
  deleteConfirmType.value = contextMenuType.value
  if (contextMenuType.value === 'folder') {
    deleteConfirmTarget.value = contextMenuTarget.value as Folder
  } else {
    deleteConfirmTarget.value = contextMenuTarget.value as FileItem
  }
  showDeleteConfirm.value = true
  hideContextMenu()
}

// 确认删除
const confirmDelete = async () => {
  if (!deleteConfirmTarget.value || !deleteConfirmType.value) return
  
  deleteLoading.value = true
  try {
    await deleteItem(deleteConfirmType.value, deleteConfirmTarget.value.id)
  } finally {
    deleteLoading.value = false
    showDeleteConfirm.value = false
    deleteConfirmTarget.value = null
    deleteConfirmType.value = null
  }
}

// 取消删除
const cancelDelete = () => {
  showDeleteConfirm.value = false
  deleteConfirmTarget.value = null
  deleteConfirmType.value = null
}

// 点击页面其他地方隐藏右键菜单
onMounted(() => {
  fetchFolders()
  fetchFiles(0)
  document.addEventListener('click', hideContextMenu)
})
</script>

<template>
  <div class="files-page">
    <!-- 左侧文件夹列表 -->
    <aside class="folder-sidebar">
      <div class="sidebar-header">
        <h3>文件夹</h3>
        <button class="add-folder-btn" @click="showNewFolderDialog = true" title="新建文件夹">+</button>
      </div>
      <div v-if="foldersLoading" class="sidebar-loading">
        <div class="loading-spinner"></div>
        <span>加载中...</span>
      </div>
      <nav v-else class="folder-list">
        <button
          v-for="folder in folders"
          :key="folder.id"
          class="folder-item"
          :class="{ active: activeFolder === folder.id }"
          @click="switchFolder(folder.id)"
          @contextmenu.prevent="showFolderContextMenu($event, folder)"
        >
          <span class="folder-icon">📁</span>
          <span class="folder-name">{{ folder.name }}</span>
          <span class="folder-count">{{ getFolderCount(folder) }}</span>
        </button>
      </nav>
    </aside>

    <!-- 右侧文件列表 -->
    <main class="files-content">
      <div class="files-header">
        <h2>{{ folders.find(f => f.id === activeFolder)?.name }}</h2>
        <div class="header-actions" :class="{ 'uploading': uploadLoading }">
          <button 
            v-if="activeFolder !== 0"
            class="upload-btn" 
            :class="{ 'uploading': uploadLoading }"
            :disabled="uploadLoading" 
            @click="triggerUpload"
          >
            <span v-if="!uploadLoading">+ 上传文件</span>
            <span v-else class="upload-btn-text">
              上传中 {{ uploadProgress }}%
            </span>
            <div v-if="uploadLoading" class="upload-btn-progress" :style="{ width: uploadProgress + '%' }"></div>
          </button>
        </div>
      </div>
      <div class="files-list">
        <div class="file-header">
          <span class="col-name">文件名</span>
          <span class="col-size">大小</span>
          <span class="col-date">修改时间</span>
        </div>
        
        <!-- 加载状态 -->
        <div v-if="loading" class="files-loading">
          <div class="loading-spinner"></div>
          <span>加载中...</span>
        </div>
        
        <!-- 空状态 -->
        <div v-else-if="filteredFiles.length === 0" class="loading">
          暂无文件
        </div>
        
        <!-- 文件列表 -->
        <div
          v-for="file in filteredFiles"
          v-else
          :key="file.id"
          class="file-row"
          :class="{ clickable: isPreviewable(file) }"
          @click="isPreviewable(file) && openPreview(file)"
          @contextmenu.prevent="showFileContextMenu($event, file)"
        >
          <div class="file-info">
            <span class="file-icon">{{ getFileIcon(file.type) }}</span>
            <span class="file-name">{{ file.original_name }}</span>
            <span v-if="isPreviewable(file)" class="preview-hint">👁️</span>
          </div>
          <span class="file-size">{{ file.size_format }}</span>
          <span class="file-date">{{ formatDate(file.updated_at) }}</span>
        </div>
      </div>
    </main>

    <!-- 右键菜单 -->
    <div
      v-if="contextMenuVisible"
      class="context-menu"
      :style="{ left: contextMenuX + 'px', top: contextMenuY + 'px' }"
    >
      <div v-if="contextMenuType === 'file'" class="context-menu-item" @click="downloadFile">
        <span class="menu-icon">⬇️</span>
        <span>下载</span>
      </div>
      <div class="context-menu-item" @click="startRename">
        <span class="menu-icon">✏️</span>
        <span>重命名</span>
      </div>
      <div class="context-menu-item delete" @click="doDelete">
        <span class="menu-icon">🗑️</span>
        <span>删除</span>
      </div>
    </div>

    <!-- 删除确认弹窗 -->
    <div v-if="showDeleteConfirm" class="modal-overlay" @click.self="cancelDelete">
      <div class="modal-content delete-modal-content">
        <div class="modal-header">
          <h3>{{ deleteConfirmType === 'folder' ? '删除文件夹' : '删除文件' }}</h3>
          <button class="close-btn" @click="cancelDelete">×</button>
        </div>
        <div class="modal-body">
          <p class="confirm-text">
            <template v-if="deleteConfirmType === 'folder'">
              确定要删除文件夹"{{ (deleteConfirmTarget as Folder)?.name }}"吗？<br>
              <span class="warning-text">文件夹内的文件将变为未分类状态</span>
            </template>
            <template v-else>
              确定要删除文件"{{ (deleteConfirmTarget as FileItem)?.original_name }}"吗？<br>
              <span class="warning-text">此操作不可恢复</span>
            </template>
          </p>
        </div>
        <div class="modal-footer">
          <button class="btn ghost" @click="cancelDelete">取消</button>
          <button class="btn danger" :disabled="deleteLoading" @click="confirmDelete">
            <span v-if="deleteLoading" class="btn-loading"></span>
            <span v-else>删除</span>
          </button>
        </div>
      </div>
    </div>

    <!-- 重命名弹窗 -->
    <div v-if="renamingItem" class="preview-overlay" @click.self="cancelRename">
      <div class="preview-content" style="max-width: 400px;">
        <div class="preview-header">
          <h3>重命名</h3>
          <button class="close-btn" @click="cancelRename">×</button>
        </div>
        <div class="preview-body" style="padding: 1.5rem;">
          <div class="form-group">
            <label>{{ renamingItem?.type === 'folder' ? '文件夹名称' : '文件名称' }}</label>
            <input
              v-model="renameValue"
              type="text"
              placeholder="请输入新名称"
              class="folder-input"
            />
          </div>
          <!-- 文件夹重命名时显示英文名称输入 -->
          <div v-if="renamingItem?.type === 'folder'" class="form-group" style="margin-top: 1rem;">
            <label>英文名称（上传用）</label>
            <input
              v-model="renameFolderValue"
              type="text"
              placeholder="请输入英文名称，用于上传路径"
              class="folder-input"
              @keyup.enter="doRename"
            />
          </div>
          <div class="dialog-actions">
            <button class="cancel-btn" @click="cancelRename">取消</button>
            <button class="confirm-btn" :disabled="renameLoading || !renameValue.trim()" @click="doRename">
              {{ renameLoading ? '保存中...' : '保存' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 隐藏的文件输入 -->
    <input
      ref="fileInputRef"
      type="file"
      style="display: none"
      @change="handleFileUpload"
    />

    <!-- 新建文件夹弹窗 -->
    <div v-if="showNewFolderDialog" class="preview-overlay" @click.self="showNewFolderDialog = false">
      <div class="preview-content" style="max-width: 400px;">
        <div class="preview-header">
          <h3>新建文件夹</h3>
          <button class="close-btn" @click="showNewFolderDialog = false">×</button>
        </div>
        <div class="preview-body" style="padding: 1.5rem;">
          <div class="form-group">
            <label>文件夹名称</label>
            <input
              v-model="newFolderName"
              type="text"
              placeholder="请输入文件夹名称"
              class="folder-input"
            />
          </div>
          <div class="form-group" style="margin-top: 1rem;">
            <label>英文名称（上传用）</label>
            <input
              v-model="newFolderNameEn"
              type="text"
              placeholder="请输入英文名称，用于上传路径"
              class="folder-input"
              @keyup.enter="createFolder"
            />
          </div>
          <div class="dialog-actions">
            <button class="cancel-btn" @click="showNewFolderDialog = false">取消</button>
            <button class="confirm-btn" :disabled="creatingFolder || !newFolderName.trim()" @click="createFolder">
              {{ creatingFolder ? '创建中...' : '创建' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 文件预览弹窗 -->
    <div v-if="showPreview" class="preview-overlay" @click.self="closePreview">
      <div class="preview-content">
        <div class="preview-header">
          <h3>{{ previewFile?.name }}</h3>
          <button class="close-btn" @click="closePreview">×</button>
        </div>
        <div class="preview-body">
          <!-- 图片预览 -->
          <div v-if="previewFile?.type === 'image'" class="image-preview">
            <img :src="getFullFileUrl(previewFile.url)" :alt="previewFile.name" style="max-width: 100%; max-height: 70vh;" />
          </div>
          
          <!-- 视频预览 -->
          <div v-else-if="previewFile?.type === 'video'" class="video-preview">
            <video controls class="video-player" style="display: block; width: 100%; max-height: 70vh;">
              <source :src="getFullFileUrl(previewFile.url)" :type="previewFile.mime_type">
              <p>您的浏览器不支持视频播放</p>
            </video>
          </div>
          
          <!-- 音频预览 -->
          <div v-else-if="previewFile?.type === 'audio'" class="audio-preview" style="display: flex; align-items: center; justify-content: center; height: 200px;">
            <audio controls>
              <source :src="getFullFileUrl(previewFile.url)" :type="previewFile.mime_type">
              <p>您的浏览器不支持音频播放</p>
            </audio>
          </div>
          
          <!-- PDF预览 -->
          <div v-else-if="previewFile?.type === 'pdf'" class="pdf-preview" style="height: 70vh;">
            <iframe :src="getFullFileUrl(previewFile.url)" style="width: 100%; height: 100%; border: none;"></iframe>
          </div>
          
          <!-- 文本预览 -->
          <div v-else-if="previewFile?.type === 'doc' || previewFile?.name?.endsWith('.txt')" class="text-preview">
            <pre>{{ previewContent || '文本内容加载中...' }}</pre>
          </div>
          
          <!-- 其他文件 -->
          <div v-else class="unsupported-preview">
            <span class="placeholder-icon">📄</span>
            <p>该文件类型暂不支持预览</p>
            <p class="placeholder-name">{{ previewFile?.name }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.files-page {
  display: flex;
  height: 100%;
  background: #f5f5f5;
}

/* 左侧文件夹栏 */
.folder-sidebar {
  width: 240px;
  background: #fff;
  border-right: 1px solid #e0e0e0;
  flex-shrink: 0;
}

.sidebar-header {
  padding: 1rem;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.sidebar-header h3 {
  margin: 0;
  font-size: 1rem;
  color: #333;
}

.add-folder-btn {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #42b883;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.2s;
}

.add-folder-btn:hover {
  background: #369970;
}

.folder-list {
  padding: 0.5rem 0;
}

.folder-item {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: background 0.2s;
  gap: 0.75rem;
}

.folder-item:hover {
  background: #f5f5f5;
}

.folder-item.active {
  background: #e8f5e9;
  color: #42b883;
}

.folder-icon {
  font-size: 1.2rem;
}

.folder-name {
  flex: 1;
  font-size: 0.9rem;
  text-align: left;
}

.folder-count {
  font-size: 0.8rem;
  color: #999;
  background: #f0f0f0;
  padding: 0.1rem 0.5rem;
  border-radius: 10px;
}

.folder-item.active .folder-count {
  background: #42b883;
  color: #fff;
}

/* 右侧文件内容 */
.files-content {
  flex: 1;
  padding: 1.5rem;
  overflow-y: auto;
}

.files-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  gap: 1rem;
}

.files-header h2 {
  margin: 0;
  font-size: 1.25rem;
  color: #333;
  white-space: nowrap;
  flex-shrink: 0;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
}

.header-actions.uploading {
  width: 100%;
  transition: width 0.3s ease;
}

.upload-btn, .new-folder-btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.upload-btn {
  background: #42b883;
  color: #fff;
  position: relative;
  overflow: hidden;
}

.upload-btn:hover {
  background: #369970;
}

.upload-btn.uploading {
  width: 100%;
  background: #a8d5c2;
  cursor: not-allowed;
  display: flex;
  align-items: center;
  justify-content: center;
}

.upload-btn-text {
  position: relative;
  z-index: 2;
  color: #fff;
  font-weight: 500;
}

.upload-btn-progress {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background: #42b883;
  transition: width 0.1s linear;
  z-index: 1;
}

.new-folder-btn {
  background: #f0f0f0;
  color: #666;
}

.new-folder-btn:hover {
  background: #e0e0e0;
}

/* 文件列表 */
.files-list {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.file-header {
  display: flex;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e0e0e0;
  color: #999;
  font-size: 0.85rem;
  font-weight: 500;
}

.file-row {
  display: flex;
  align-items: center;
  padding: 0.875rem 1rem;
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.2s;
}

.file-row:hover {
  background: #f9f9f9;
}

.file-row:last-child {
  border-bottom: none;
}

.col-name, .file-info {
  flex: 1;
}

.col-size, .file-size {
  width: 100px;
  text-align: right;
}

.col-date, .file-date {
  width: 120px;
  text-align: right;
}

.file-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.file-icon {
  font-size: 1.25rem;
}

.file-name {
  font-size: 0.9rem;
  color: #333;
}

.file-size, .file-date {
  font-size: 0.85rem;
  color: #999;
}

/* 可点击的文件行 */
.file-row.clickable {
  cursor: pointer;
}

.file-row.clickable:hover {
  background: #e8f5e9;
}

.preview-hint {
  font-size: 0.9rem;
  opacity: 0.5;
  margin-left: 0.5rem;
}

/* 预览弹窗 */
.preview-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.preview-content {
  background: #fff;
  border-radius: 8px;
  width: 80%;
  max-width: 800px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #e0e0e0;
}

.preview-header h3 {
  margin: 0;
  font-size: 1rem;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #999;
  cursor: pointer;
  line-height: 1;
}

.close-btn:hover {
  color: #666;
}

.preview-body {
  flex: 1;
  overflow: auto;
  padding: 1rem;
  min-height: 300px;
}

/* 图片预览 */
.image-preview, .video-preview, .unsupported-preview {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 300px;
}

.placeholder-image, .video-placeholder, .unsupported-preview {
  text-align: center;
  color: #999;
}

.placeholder-icon {
  font-size: 4rem;
  display: block;
  margin-bottom: 1rem;
}

.placeholder-name {
  font-size: 0.85rem;
  color: #666;
  margin-top: 0.5rem;
}

/* 视频播放器 */
.video-player {
  width: 100%;
  max-height: 500px;
  display: none;
}

.video-placeholder {
  padding: 2rem;
}

/* 文本预览 */
.text-preview {
  background: #f5f5f5;
  border-radius: 4px;
  padding: 1rem;
}

.text-preview pre {
  margin: 0;
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 0.9rem;
  line-height: 1.6;
  color: #333;
  white-space: pre-wrap;
  word-wrap: break-word;
}

/* 新建文件夹弹窗样式 */
.folder-input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 0.9rem;
  box-sizing: border-box;
  transition: border-color 0.2s;
}

.folder-input:focus {
  outline: none;
  border-color: #42b883;
}

.form-group label {
  display: block;
  font-size: 0.85rem;
  color: #666;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #f0f0f0;
}

.cancel-btn {
  padding: 0.6rem 1.5rem;
  border: 1px solid #e0e0e0;
  background: #f5f5f5;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  color: #666;
  transition: all 0.2s;
}

.cancel-btn:hover {
  background: #e8e8e8;
  border-color: #d0d0d0;
}

.confirm-btn {
  padding: 0.6rem 1.5rem;
  border: none;
  background: #42b883;
  color: #fff;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(66, 184, 131, 0.3);
}

.confirm-btn:hover:not(:disabled) {
  background: #369970;
  box-shadow: 0 4px 8px rgba(66, 184, 131, 0.4);
}

.confirm-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.confirm-btn.delete {
  background: #f44336;
}

.confirm-btn.delete:hover:not(:disabled) {
  background: #d32f2f;
}

/* 弹窗样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: #fff;
  border-radius: 12px;
  max-width: 480px;
  width: 90%;
  max-height: 80vh;
  overflow: hidden;
}

.delete-modal-content {
  max-width: 400px;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e8e8e8;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.1rem;
  color: #333;
}

.modal-body {
  padding: 1.5rem;
}

.confirm-text {
  margin: 0;
  color: #666;
  line-height: 1.6;
  font-size: 0.95rem;
}

.warning-text {
  color: #f44336;
  font-size: 0.9rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e8e8e8;
}

.btn {
  padding: 0.5rem 1.25rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.2s;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn.ghost {
  background: #f2f2f2;
  color: #666;
}

.btn.ghost:hover {
  background: #e0e0e0;
}

.btn.danger {
  background: #f44336;
  color: #fff;
}

.btn.danger:hover:not(:disabled) {
  background: #d32f2f;
}

.btn-loading {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* 加载状态 */
.loading {
  text-align: center;
  padding: 2rem;
  color: #999;
}

.sidebar-loading,
.files-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  gap: 1rem;
  color: #999;
}

.sidebar-loading {
  min-height: 200px;
}

.files-loading {
  min-height: 300px;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e0e0e0;
  border-top-color: #42b883;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* 右键菜单 */
.context-menu {
  position: fixed;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  z-index: 1001;
  min-width: 120px;
  padding: 0.25rem 0;
}

.context-menu-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  cursor: pointer;
  font-size: 0.85rem;
  color: #333;
  transition: background 0.2s;
}

.context-menu-item:hover {
  background: #f5f5f5;
}

.context-menu-item.delete {
  color: #f44336;
}

.context-menu-item.delete:hover {
  background: #ffebee;
}

.menu-icon {
  font-size: 0.9rem;
}
</style>
